import { LanguagesEnum } from '@/api/enums';
import { ListingRequest, ListingResponse } from '@/api/types';
import { Account } from '@/client/accounts/types/account.type';
import { MessageResponseModel } from '@/client/models';
import { Tag } from '@/client/tags';
import { User } from '@/client/users';
import { GenericForbiddenApiError } from '@/hooks/query/types';
import { UseMutationResult } from 'react-query';

export enum AdminTaskStatus {
  TODO = 'todo',
  COMPLETED = 'completed',
  SKIPPED = 'skipped',
}

export enum AdminTaskKey {
  SETUP_ACTIVE_DIRECTORY = 'setup-active-directory',
  WHITELISTING = 'whitelisting',
  CREATE_ADMIN_USERS = 'create-admin-users',
  UPLOAD_LOGO_AND_SET_IT_RESPONSIBLE = 'upload-logo-and-set-it-responsible',
  CREATE_USERS = 'create-users',
  INFORM_USERS_FOR_COLLABORATION = 'inform-users-for-collaboration',
  COMPLETE_SECURITY_PROFILE = 'complete-security-profile',
  // Free Trial Tasks
  SETUP_YOUR_ACCOUNT = 'setup-your-account',
  CUSTOMIZE_A_COURSE = 'customize-course',
  SEE_PHISHING_CATALOGUE = 'check-phishing-catalogue',
  CREATE_FIRST_USER = 'create-first-user',
}

export type UpdateAccountAdminTaskRequest = {
  id: string;
  status: AdminTaskStatus;
};

export type AccountAdminTask = {
  id?: string;
  task: AdminTask;
  user: User;
  account: Account;
  status: AdminTaskStatus;
  statusChangedOn?: Date | null;
};

export type AdminTasksResultWithCount = ListingResponse<AdminTask>;

export type AdminTask = {
  id?: string;
  key?: string;
  active?: boolean;
  created?: Date;
  updated?: Date | null;
  buttonUrl?: string | null;
  isForFreeTrial?: boolean;
  visibilityGroup?: AdminTaskVisibilityGroup;
  translations?: AdminTaskTranslation[];
  tags?: Tag[];
  accountAdminTasks?: AccountAdminTask[];
  visibilityRules?: AdminTaskRule[];
  order?: number;
  featureFlagKey?: string | null;
};

export type AdminTaskPayload = Omit<AdminTask, 'tags'> & { tags?: string[] };

export type AdminTaskVisibilityGroup = {
  enabled: boolean;
  condition: AdminTaskVisibilityGroupCondition | null;
};

export type AdminTaskTranslation = {
  id?: string;
  language: LanguagesEnum;
  name: string;
  description: string;
  buttonName: string;
};

export type AdminTaskRule = {
  id: string;
  product: AdminTaskRuleProduct[];
  filterBy: AdminTaskRuleFilterBy;
  operator: AdminTaskRuleOperator;
  status: AdminTaskRuleAccountStatus[];
  dateValue: string | null;
};

export type EditableTaskTranslationFields = {
  title: string;
  description: string;
  buttonName: string;
};

export type EditableTaskFormState = {
  order?: number;
  taskId: string;
  active: boolean;
  // translations
  taskLanguage: string;
  title: string;
  description: string;
  buttonName: string;
  translationsMap: Record<string, EditableTaskTranslationFields>;
  //
  buttonUrl: string;
  tags: Tag[];
  isForFreeTrial: boolean;
  visibilityRulesEnabled: boolean;
  matchCondition: AdminTaskVisibilityGroupCondition;
  visibilityRules: AdminTaskRule[];
  featureFlagKey?: string | null;
};

export enum AdminTaskVisibilityGroupCondition {
  AND = 'AND',
  OR = 'OR',
}

export enum AdminTaskRuleFilterBy {
  STATUS = 'status',
  ONBOARDING_DATE = 'onboarding-date',
  ACCOUNT_CREATION_DATE = 'account-creation-date',
}

export enum AdminTaskRuleOperator {
  IS = 'is',
  IS_NOT = 'is-not',
  AFTER = 'after',
  BEFORE = 'before',
}

export enum AdminTaskRuleProduct {
  AWT = 'awt',
  PHISHING = 'phishing',
  SECURITY_CULTURE = 'security-culture',
  AUTO_PHISHING = 'auto-phishing',
  PHISHING_ONE_OFF = 'phishing-one-off',
  FREE_BASELINE_MEASUREMENT = 'free-baseline-measurement',
  ACCOUNT = 'account',
}

export enum AdminTaskRuleAccountStatus {
  ONBOARDING = 'onboarding',
  OFFBOARDING = 'offboarding',
  ACTIVE = 'active',
  INACTIVE = 'inactive',
  FREE_TRIAL = 'free-trial',
}

export enum AdminTaskTagEnum {
  RECOMMENDED_GO_LIVE = 'recommended-go-live',
  REQUIRED_GO_LIVE = 'required-go-live',
  NONE = '',
}

export type ListAdminTasksRequest = ListingRequest;
export type ListAdminTasksResponse = ListingResponse<AccountAdminTask>;

// Admin Task Mutation Types
export type CreateAdminTaskMutation = UseMutationResult<
  AdminTask,
  GenericForbiddenApiError,
  AdminTaskPayload
>;

export type UpdateAdminTaskMutation = UseMutationResult<
  AdminTask,
  GenericForbiddenApiError,
  { taskId: string } & AdminTaskPayload
>;

export type DeleteAdminTaskMutation = UseMutationResult<
  MessageResponseModel,
  GenericForbiddenApiError,
  { taskId: string }
>;

// Account Admin Task Mutation Types
export type CreateAccountAdminTaskMutation = UseMutationResult<
  AccountAdminTask,
  GenericForbiddenApiError,
  AccountAdminTask & { accountId: string }
>;

export type UpdateAccountAdminTaskMutation = UseMutationResult<
  AccountAdminTask,
  GenericForbiddenApiError,
  UpdateAccountAdminTaskRequest & { accountId: string }
>;

export type DeleteAccountAdminTaskMutation = UseMutationResult<
  MessageResponseModel,
  GenericForbiddenApiError,
  { taskId: string; accountId: string }
>;
